<template>
  <b-modal
    v-model="visible"
    size="lg"
    body-class="bg-white ai-call-modal-body"
    modal-class="ai-call-modal-wrapper"
    no-close-on-backdrop
    no-close-on-esc
    :hide-footer="true"
    @hide="onHide"
  >
    <template #modal-title>
      <span v-if="step === 'pick-list'">📞 AI Call — Choose a list</span>
      <span v-else-if="step === 'configure'">⚙️ {{ listTypeLabel }} — Filters</span>
      <span v-else-if="step === 'select' && mode === 'one'">📞 AI Call a Patient</span>
      <span v-else-if="step === 'select' && mode === 'batch'">📋 AI Call a Batch</span>
      <span v-else-if="step === 'running'">
        <i class="fa fa-phone fa-flip-horizontal text-success"></i>
        AI Call in progress…
      </span>
      <span v-else-if="step === 'finished'">✅ AI Call finished</span>
    </template>

    <!-- Step 1: pick which list to pull from -->
    <div v-if="step === 'pick-list'">
      <p class="text-muted">Which list of patients would you like to call?</p>
      <div class="ai-call-list-choices">
        <button v-for="opt in listTypeOptions" :key="opt.value"
          :class="['ai-call-list-choice', { disabled: !opt.enabled }]"
          :disabled="!opt.enabled"
          @click="opt.enabled && chooseListType(opt.value)">
          <div class="ai-call-list-choice-icon">{{ opt.icon }}</div>
          <div class="ai-call-list-choice-label">{{ opt.label }}</div>
          <div class="ai-call-list-choice-sub text-muted">{{ opt.subtitle }}</div>
        </button>
      </div>
      <div class="text-right mt-3">
        <b-button variant="link" size="sm" @click="cancel">Cancel</b-button>
      </div>
    </div>

    <!-- Step 1.5: configure filters (providers + date range) before we call
         /runquery. Mirrors the Admin-page filters for each list type. -->
    <div v-else-if="step === 'configure'">
      <h6 class="mb-3">Configure {{ listTypeLabel }} filters</h6>

      <div class="form-group mb-3" v-if="currentListConfig && currentListConfig.filters.includes('providers')">
        <label class="small text-muted d-block">Providers</label>
        <div class="ai-call-provider-picker">
          <div v-if="providerOptions.length === 0" class="text-muted small py-2">
            No providers loaded for this site yet. Try refreshing the page.
          </div>
          <template v-else>
            <div class="ai-call-provider-select-all">
              <b-form-checkbox
                :checked="allProvidersSelected"
                @change="toggleAllProviders"
              ><strong>Select all</strong> ({{ selectedProviderNums.length }} of {{ providerOptions.length }})</b-form-checkbox>
            </div>
            <div class="ai-call-provider-list">
              <div v-for="p in providerOptions" :key="p.ProvNum" class="ai-call-provider-item">
                <b-form-checkbox
                  :checked="isProviderSelected(p.ProvNum)"
                  @change="toggleProvider(p.ProvNum, $event)"
                >{{ formatProviderName(p) }}</b-form-checkbox>
              </div>
            </div>
          </template>
        </div>
      </div>

      <div class="row mb-3">
        <div class="col-md-6">
          <label class="small text-muted">Date from</label>
          <b-form-input v-model="filterDateFrom" type="date" size="sm"></b-form-input>
        </div>
        <div class="col-md-6">
          <label class="small text-muted">Date to</label>
          <b-form-input v-model="filterDateTo" type="date" size="sm"></b-form-input>
        </div>
      </div>

      <div class="text-right mt-3 pt-3 border-top">
        <b-button variant="link" size="sm" @click="step = 'pick-list'">← Back</b-button>
        <b-button variant="outline-secondary" size="sm" @click="cancel" class="mr-2">Cancel</b-button>
        <b-button variant="primary" size="sm"
          :disabled="!canLoadPatients"
          @click="loadList">
          Load patients
        </b-button>
      </div>
    </div>

    <!-- Step 2: mode + search/preview + options -->
    <div v-else-if="step === 'select'">
      <div v-if="loading" class="text-center py-4">
        <i class="fa fa-spinner fa-spin"></i> Loading {{ listTypeLabel }} list...
      </div>
      <div v-else-if="loadError" class="text-danger py-2">
        <i class="fa fa-warning"></i> Could not load the list: {{ loadError }}
      </div>
      <div v-else>
        <!-- Mode switch -->
        <div class="ai-call-mode-switch mb-3">
          <button
            :class="['ai-call-mode-btn', { active: mode === 'one' }]"
            @click="mode = 'one'"
          >👤 One patient</button>
          <button
            :class="['ai-call-mode-btn', { active: mode === 'batch' }]"
            @click="mode = 'batch'"
          >👥 Everyone on this list ({{ patients.length }})</button>
        </div>

        <!-- ONE-PATIENT MODE -->
        <div v-if="mode === 'one'">
          <b-form-input v-model="search" placeholder="Search by name or PatNum..." class="mb-2" autofocus></b-form-input>
          <div class="ai-call-patient-list">
            <div v-if="filteredPatients.length === 0" class="text-muted small py-2">
              No matching patients on this list.
            </div>
            <div v-for="p in filteredPatients.slice(0, 30)" :key="p.PatNum"
              :class="['ai-call-patient-row', { selected: selectedPatNum === p.PatNum }]"
              @click="selectedPatNum = p.PatNum"
            >
              <div class="ai-call-patient-name">{{ formatPatientName(p) }}</div>
              <div class="ai-call-patient-meta">
                <span class="text-muted">PatNum: {{ p.PatNum }}</span>
                <span v-if="p.Phone || p.WirelessPhone" class="ml-3 text-muted">{{ p.Phone || p.WirelessPhone }}</span>
                <span v-if="p.DateDue" class="ml-3"><b-badge variant="warning">Due {{ p.DateDue }}</b-badge></span>
              </div>
            </div>
            <div v-if="filteredPatients.length > 30" class="text-muted small py-2">
              Showing first 30 of {{ filteredPatients.length }} matches. Refine your search.
            </div>
          </div>
        </div>

        <!-- BATCH MODE -->
        <div v-else>
          <div class="d-flex align-items-center mb-2">
            <b-form-checkbox v-model="allChecked" @change="toggleAll" class="mr-2">
              Select all ({{ selectedBatchPatNums.length }} of {{ patients.length }} selected)
            </b-form-checkbox>
            <div class="ml-auto">
              <small v-if="exceedsDailyCap" class="text-warning">
                ⚠ Exceeds daily cap ({{ maxDailyCallsFromSite }}).
                <b-form-checkbox v-model="overrideDailyCap" class="d-inline-block ml-1">Override</b-form-checkbox>
              </small>
            </div>
          </div>
          <div class="ai-call-patient-list">
            <div v-for="p in patients" :key="p.PatNum" class="ai-call-patient-row batch-row">
              <b-form-checkbox
                :checked="isBatchChecked(p.PatNum)"
                @change="toggleBatchPatient(p.PatNum, $event)"
              >
                <span class="ai-call-patient-name">{{ formatPatientName(p) }}</span>
                <span class="ai-call-patient-meta ml-2">
                  <span class="text-muted">PatNum: {{ p.PatNum }}</span>
                  <span v-if="p.Phone || p.WirelessPhone" class="ml-2 text-muted">{{ p.Phone || p.WirelessPhone }}</span>
                  <span v-if="p.DateDue" class="ml-2"><b-badge variant="warning">Due {{ p.DateDue }}</b-badge></span>
                </span>
              </b-form-checkbox>
            </div>
          </div>
        </div>

        <!-- Shared options -->
        <div class="ai-call-options mt-3 pt-3 border-top">
          <div class="row">
            <div class="col-md-12">
              <label class="small text-muted">Call reason</label>
              <b-form-select v-model="callReason" size="sm">
                <option value="recall">Recall (overdue cleaning)</option>
                <option value="treatment">Treatment (unscheduled)</option>
              </b-form-select>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="text-right mt-3 pt-3 border-top">
          <b-button variant="link" size="sm" @click="step = 'configure'">← Back</b-button>
          <b-button variant="outline-secondary" size="sm" @click="cancel" class="mr-2">Cancel</b-button>
          <b-button variant="primary" size="sm"
            :disabled="!canStart"
            @click="onStart">
            <i class="fa fa-phone fa-flip-horizontal"></i>
            {{ mode === 'one' ? 'Call patient' : `Call ${selectedBatchPatNums.length} patients` }}
          </b-button>
        </div>
      </div>
    </div>

    <!-- Step 3: run progress -->
    <div v-else-if="step === 'running' || step === 'finished'">
      <div v-if="mode === 'one'">
        <p v-if="step === 'running'">
          <i class="fa fa-spinner fa-spin"></i> Calling {{ runningPatientName }}…
        </p>
        <div v-if="singleResult" class="mt-2">
          <p>
            <strong>Result:</strong>
            <b-badge :variant="singleResultVariant">{{ singleResult.reason || singleResult.outcome || (singleResult.success ? 'initiated' : 'failed') }}</b-badge>
            <strong class="ml-3">Mode:</strong> {{ singleResult.mode || '—' }}
          </p>
          <p v-if="singleResult.message" class="text-muted small">{{ singleResult.message }}</p>
        </div>
      </div>

      <div v-else>
        <!-- Batch progress -->
        <div class="d-flex align-items-center mb-2">
          <div>
            <strong>Status:</strong>
            <b-badge :variant="batchStatusVariant">{{ batchStatus }}</b-badge>
          </div>
          <div class="ml-3 text-muted small">
            {{ batchTotals.attempted }} / {{ batchTotals.queued }} attempted
            · {{ batchTotals.succeeded }} initiated
            · {{ batchTotals.skipped }} skipped
          </div>
          <div class="ml-auto">
            <b-button
              v-if="step === 'running'"
              variant="outline-danger" size="sm"
              :disabled="aborting"
              @click="onAbort">
              <i class="fa fa-stop"></i> {{ aborting ? 'Stopping…' : 'Stop batch' }}
            </b-button>
          </div>
        </div>
        <b-progress :max="batchTotals.queued || 1" class="mb-3" height="6px">
          <b-progress-bar :value="batchTotals.attempted" variant="primary"></b-progress-bar>
        </b-progress>

        <div class="ai-call-progress-list">
          <div v-for="c in batchCalls" :key="c.id" class="ai-call-progress-row">
            <div class="ai-call-progress-name">PatNum {{ c.patientId }}</div>
            <div class="ai-call-progress-meta">
              <b-badge v-if="c.outcome" :variant="outcomeVariant(c.outcome)">{{ c.outcome }}</b-badge>
              <b-badge v-else variant="info">in progress…</b-badge>
              <span v-if="c.durationSeconds" class="ml-2 text-muted small">{{ c.durationSeconds }}s</span>
            </div>
          </div>
          <div v-if="batchCalls.length === 0 && step === 'running'" class="text-muted small py-2">
            <i class="fa fa-spinner fa-spin"></i> Waiting for first call…
          </div>
        </div>

        <div v-if="skippedReasons.length > 0" class="mt-3 small text-muted">
          <strong>Skipped:</strong>
          <span v-for="(s, i) in skippedReasons" :key="i" class="d-block">
            PatNum {{ s.patNum }}: {{ s.message || s.reason }}
          </span>
        </div>
      </div>

      <div class="text-right mt-3 pt-3 border-top">
        <b-button v-if="step === 'finished'" variant="primary" size="sm" @click="close">Close</b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  triggerAutorecallCall,
  triggerAutorecallBatch,
  abortAutorecallBatch,
  pollAutorecallBatchStatus
} from '@/api/autorecall';

const POLL_INTERVAL_MS = 3000;

// Per-list-type metadata. One place to add a new list type:
//   - queryType: what to send as `type:` to /runquery (must match backend switch case)
//   - callReason: what to send to /trigger-autorecall-call or /autorecall-batch
//                 Must be a reason the AutoRecall Lambda knows how to open a call for.
//   - filters: which filters the Configure step should render + send in the query
//              Supported: 'providers' | 'dateRange'. Extend when we need more.
//   - enabled: false = greyed-out "coming soon" choice. Use when the pickable
//              visual is ready but backend support is pending.
const LIST_TYPES = {
  OVERDUE_RECALL: {
    value: 'OVERDUE_RECALL',
    label: 'Overdue recall',
    icon: '🦷',
    subtitle: 'Patients due for their cleaning',
    queryType: 'OVERDUE_RECALL',
    callReason: 'recall',
    filters: ['providers', 'dateRange'],
    enabled: true
  },
  UNSCHEDULED_TREATMENT: {
    value: 'UNSCHEDULED_TREATMENT',
    label: 'Unscheduled treatment',
    icon: '🩺',
    subtitle: 'Patients with pending treatment plans',
    queryType: 'UNSCHEDULED_TREATMENT',
    callReason: 'treatment',
    filters: ['providers', 'dateRange'],
    enabled: true
  },
  BROKEN_APPOINTMENTS: {
    value: 'BROKEN_APPOINTMENTS',
    label: 'Broken appointments',
    icon: '⏰',
    subtitle: 'Coming soon — needs dedicated call script + follow-up scheduling',
    queryType: 'BROKEN_APPOINTMENTS',
    callReason: null, // backend doesn't support 'broken_appointment' yet
    filters: ['dateRange'],
    enabled: false
  }
};

const LIST_TYPE_OPTIONS = Object.values(LIST_TYPES);

export default {
  name: 'AICallModal',
  props: {
    value: { type: Boolean, default: false },
    siteId: { type: String, default: '' }
  },
  data() {
    return {
      visible: false,
      step: 'pick-list',          // 'pick-list' | 'configure' | 'select' | 'running' | 'finished'
      mode: 'one',                // 'one' | 'batch'
      listType: null,
      listTypeOptions: LIST_TYPE_OPTIONS,

      // Configure step — providers + date range (mirrors what the Admin pages require)
      providerOptions: [],        // [{ProvNum, FName, LName, IsSecondary, ...}]
      selectedProviderNums: [],
      filterDateFrom: '',
      filterDateTo: '',

      // Select step
      loading: false,
      loadError: null,
      patients: [],

      // One-patient mode
      search: '',
      selectedPatNum: null,

      // Batch mode
      batchSelection: {},
      allChecked: true,
      overrideDailyCap: false,

      // Shared options
      callReason: 'recall',

      // Run state
      batchRunId: null,
      singleResult: null,
      runningPatientName: '',
      batchStatus: null,
      batchCalls: [],
      batchTotals: { queued: 0, attempted: 0, succeeded: 0, skipped: 0 },
      skippedReasons: [],
      pollTimer: null,
      aborting: false
    };
  },
  computed: {
    listTypeLabel() {
      return (this.currentListConfig && this.currentListConfig.label) || this.listType;
    },
    filteredPatients() {
      const q = (this.search || '').trim().toLowerCase();
      if (!q) return this.patients;
      if (/^\d+$/.test(q)) {
        return this.patients.filter(p => String(p.PatNum).includes(q));
      }
      return this.patients.filter(p => {
        const name = this.formatPatientName(p).toLowerCase();
        return name.includes(q);
      });
    },
    selectedBatchPatNums() {
      if (this.allChecked) {
        return this.patients
          .map(p => p.PatNum)
          .filter(pn => this.batchSelection[pn] !== false);
      }
      return this.patients
        .map(p => p.PatNum)
        .filter(pn => this.batchSelection[pn] === true);
    },
    maxDailyCallsFromSite() {
      const s = this.$store.state.customer.settings || [];
      const bot = s.find(x => x.Property === 'TEAMIO_BOT_SETTINGS');
      try {
        const parsed = JSON.parse(bot && bot.PropertyValue || '{}');
        return (parsed.aiReceptionist && parsed.aiReceptionist.recall &&
                parsed.aiReceptionist.recall.maxDailyCalls) || 10;
      } catch (_) { return 10; }
    },
    exceedsDailyCap() {
      return this.mode === 'batch' && this.selectedBatchPatNums.length > this.maxDailyCallsFromSite;
    },
    canStart() {
      if (this.mode === 'one') return this.selectedPatNum != null;
      if (this.selectedBatchPatNums.length === 0) return false;
      if (this.exceedsDailyCap && !this.overrideDailyCap) return false;
      return true;
    },
    batchStatusVariant() {
      switch (this.batchStatus) {
        case 'completed': return 'success';
        case 'aborted':   return 'warning';
        case 'failed':    return 'danger';
        default:          return 'info';
      }
    },
    singleResultVariant() {
      if (!this.singleResult) return 'secondary';
      if (this.singleResult.success) return 'success';
      return 'warning';
    },
    allProvidersSelected() {
      return this.providerOptions.length > 0 &&
             this.selectedProviderNums.length === this.providerOptions.length;
    },
    currentListConfig() {
      return this.listType ? LIST_TYPES[this.listType] : null;
    },
    canLoadPatients() {
      if (!this.filterDateFrom || !this.filterDateTo) return false;
      if (this.currentListConfig && this.currentListConfig.filters.includes('providers')
          && this.selectedProviderNums.length === 0) return false;
      return true;
    }
  },
  watch: {
    value(v) { this.visible = v; if (v) this.reset(); },
    visible(v) { this.$emit('input', v); }
  },
  methods: {
    reset() {
      this.step = 'pick-list';
      this.mode = 'one';
      this.listType = null;
      this.providerOptions = [];
      this.selectedProviderNums = [];
      const today = new Date();
      const yyyyMmDd = (d) => d.toISOString().slice(0, 10);
      const twoYearsAgo = new Date(today); twoYearsAgo.setFullYear(today.getFullYear() - 2);
      const sixMonthsAhead = new Date(today); sixMonthsAhead.setMonth(today.getMonth() + 6);
      this.filterDateFrom = yyyyMmDd(twoYearsAgo);
      this.filterDateTo = yyyyMmDd(sixMonthsAhead);
      this.loading = false;
      this.loadError = null;
      this.patients = [];
      this.search = '';
      this.selectedPatNum = null;
      this.batchSelection = {};
      this.allChecked = true;
      this.overrideDailyCap = false;
      this.callReason = 'recall';
      this.batchRunId = null;
      this.singleResult = null;
      this.runningPatientName = '';
      this.batchStatus = null;
      this.batchCalls = [];
      this.batchTotals = { queued: 0, attempted: 0, succeeded: 0, skipped: 0 };
      this.skippedReasons = [];
      this.aborting = false;
      this.stopPolling();
    },
    onHide() {
      this.stopPolling();
    },
    cancel() { this.visible = false; },
    close()  { this.visible = false; },

    chooseListType(type) {
      const config = LIST_TYPES[type];
      if (!config || !config.enabled) return;
      this.listType = type;
      this.callReason = config.callReason || 'recall';
      if (config.filters.includes('providers')) {
        // Providers come from the Vuex customer store — already loaded at login.
        // Matches the backend SQL filter (IsHidden=0 AND IsNotPerson=0).
        const storeProviders = this.$store.state.customer.providers || [];
        this.providerOptions = storeProviders.filter(p =>
          p && p.ProvNum != null && !p.IsHidden && !p.IsNotPerson
        );
        this.selectedProviderNums = this.providerOptions.map(p => p.ProvNum);
      } else {
        this.providerOptions = [];
        this.selectedProviderNums = [];
      }
      this.step = 'configure';
    },

    // Build a list-type-specific /runquery payload. Any new list type just
    // adds a branch here; the rest of the modal stays the same.
    buildRunQueryPayload() {
      const config = LIST_TYPES[this.listType];
      const today = new Date();
      const yyyyMmDd = (d) => d.toISOString().slice(0, 10);
      const base = {
        type: config.queryType,
        siteID: this.siteId,
        listID: 0,
        dateFrom: this.filterDateFrom,
        dateTo: this.filterDateTo,
        _date: yyyyMmDd(today),
        country: localStorage.getItem('country') || 'USA'
      };
      if (config.filters.includes('providers')) {
        const firstSelected = this.providerOptions.find(p => this.selectedProviderNums.includes(p.ProvNum));
        const isSecondary = firstSelected && firstSelected.IsSecondary != null ? firstSelected.IsSecondary : 0;
        Object.assign(base, {
          providers: this.selectedProviderNums,
          isSecondary,
          patientsOlderThan: '',
          includeOrExcludePlan: '',
          insPlans: undefined
        });
      }
      // Per-list-type extras — UNSCHEDULED_TREATMENT has procCodes/priorities/
      // amount filters that we intentionally leave empty so the Admin-page-
      // style narrowing doesn't happen. We want "everyone with unscheduled
      // treatment" as the default. Advanced narrowing → use the Admin page.
      if (config.queryType === 'UNSCHEDULED_TREATMENT') {
        Object.assign(base, {
          procCodes: undefined,
          includeOrExclude: '',
          includeOrExcludePriority: '',
          priorities: [],
          amount: undefined
        });
      }
      // BROKEN_APPOINTMENTS has viewByAdjustment + adjustments/codes. Not
      // enabled for callReason today but keeping the shape correct for when
      // we light it up.
      if (config.queryType === 'BROKEN_APPOINTMENTS') {
        Object.assign(base, {
          viewByAdjustment: false,
          adjustments: '',
          codes: ''
        });
      }
      return base;
    },

    async loadList() {
      this.loading = true;
      this.loadError = null;
      this.step = 'select';
      try {
        const payload = this.buildRunQueryPayload();
        const response = await this.$store.dispatch('teamio/runQuery', payload);
        const body = (response && response.data) || null;
        // eslint-disable-next-line no-console
        console.log('[AICallModal] loadList response:', body);
        const arr = Array.isArray(body) ? body : (body && body.data) || null;
        // Patients always land in slot [0] across all three list types.
        const patientsList = (Array.isArray(arr) && Array.isArray(arr[0])) ? arr[0] : [];
        this.patients = patientsList.filter(p => p && p.PatNum);
        this.patients.forEach(p => { this.$set(this.batchSelection, p.PatNum, true); });
        // eslint-disable-next-line no-console
        console.log('[AICallModal] loaded', this.patients.length, 'patients. first:', this.patients[0]);
      } catch (err) {
        this.loadError = err.message || 'Failed to load list';
        // eslint-disable-next-line no-console
        console.error('[AICallModal] loadList failed:', err);
      } finally {
        this.loading = false;
      }
    },

    // Provider-picker helpers
    isProviderSelected(provNum) { return this.selectedProviderNums.includes(provNum); },
    toggleProvider(provNum, checked) {
      if (checked) {
        if (!this.selectedProviderNums.includes(provNum)) this.selectedProviderNums.push(provNum);
      } else {
        this.selectedProviderNums = this.selectedProviderNums.filter(n => n !== provNum);
      }
    },
    toggleAllProviders(checked) {
      this.selectedProviderNums = checked ? this.providerOptions.map(p => p.ProvNum) : [];
    },
    formatProviderName(p) {
      const name = [p.FName, p.MI, p.LName].filter(Boolean).join(' ');
      return p.Suffix ? `${name}, ${p.Suffix}` : name;
    },

    formatPatientName(p) {
      if (p.Patient) {
        // "Smith, Saumil" → "Saumil Smith" for natural reading
        if (p.Patient.includes(',')) {
          const [ln, fn] = p.Patient.split(',').map(s => s.trim());
          return `${fn} ${ln}`;
        }
        return p.Patient;
      }
      return [p.FName, p.LName].filter(Boolean).join(' ') || `Patient ${p.PatNum}`;
    },

    isBatchChecked(patNum) {
      if (this.allChecked) return this.batchSelection[patNum] !== false;
      return this.batchSelection[patNum] === true;
    },
    toggleBatchPatient(patNum, isChecked) {
      this.$set(this.batchSelection, patNum, !!isChecked);
    },
    toggleAll(isChecked) {
      this.allChecked = isChecked;
      this.patients.forEach(p => { this.$set(this.batchSelection, p.PatNum, !!isChecked); });
    },

    outcomeVariant(outcome) {
      if (!outcome) return 'info';
      if (['booked', 'rescheduled'].includes(outcome)) return 'success';
      if (['do_not_contact', 'declined', 'failed'].includes(outcome)) return 'danger';
      if (['voicemail', 'no_answer', 'callback_requested'].includes(outcome)) return 'warning';
      return 'secondary';
    },

    async onStart() {
      if (this.mode === 'one') return this.startOne();
      return this.startBatch();
    },

    async startOne() {
      const patient = this.patients.find(p => p.PatNum === this.selectedPatNum);
      if (!patient) return;
      this.runningPatientName = this.formatPatientName(patient);
      this.step = 'running';
      this.singleResult = null;
      try {
        const result = await triggerAutorecallCall({
          siteID: this.siteId,
          patNum: patient.PatNum,
          taskID: patient.TaskID || `adhoc-${Date.now()}`,
          callReason: this.callReason
        });
        this.singleResult = result;
        this.$emit('call-completed', { mode: 'one', patient, result });
      } catch (err) {
        this.singleResult = {
          success: false,
          reason: 'network_error',
          message: (err.response && err.response.data && err.response.data.message) || err.message
        };
      } finally {
        this.step = 'finished';
      }
    },

    async startBatch() {
      const selectedPatNums = this.selectedBatchPatNums;
      const patientsPayload = selectedPatNums.map(pn => {
        const p = this.patients.find(x => x.PatNum === pn);
        return {
          patNum: pn,
          taskID: p && p.TaskID ? p.TaskID : undefined,
          callReason: this.callReason
        };
      });
      this.batchTotals = { queued: patientsPayload.length, attempted: 0, succeeded: 0, skipped: 0 };
      this.batchCalls = [];
      this.skippedReasons = [];
      this.batchStatus = 'running';
      this.step = 'running';

      // Kick off async — do NOT await here, so UI can poll while it runs
      this.batchRunId = null;
      const batchPromise = triggerAutorecallBatch({
        siteID: this.siteId,
        patients: patientsPayload,
        callReason: this.callReason,
        initiatedBy: `user:${this.$store.state.customer.employeeID || 'unknown'}`,
        listType: this.listType
      });

      // Start polling immediately. The batch server assigns the batchRunId; we
      // discover it from the first poll response by... not being able to. So we
      // wait for the first event to arrive via the final response OR use the
      // response's batchRunId. Simpler path: polling kicks in after the first
      // /autorecall-batch-status call succeeds with the batchRunId we need.
      //
      // Trick: the server responds with batchRunId ONLY at end-of-batch. To get
      // live progress, we need the ID sooner. Approach: frontend generates no
      // ID (server does), so we poll via a second route: we start the batch and
      // grab batchRunId from any early response. Current server returns ID only
      // at completion, so for v1 we show the final rollup when the POST resolves
      // — live polling works only if the Lambda streams or we add a pre-insert.
      //
      // Pragmatic v1: await the POST. For DEV's 10-call cap + 2s pacing +
      // ~30s/call that's ~5 min worst case. Good enough; the "live" polling
      // hook is wired but gates on batchRunId which won't show up mid-run.
      // TODO: have the batch Lambda create the row and return batchRunId BEFORE
      // starting the runner (requires async invoke pattern).

      try {
        const result = await batchPromise;
        this.batchRunId = result.batchRunId;
        this.batchStatus = result.aborted ? 'aborted' : (result.completed ? 'completed' : 'failed');
        this.batchTotals = {
          queued: patientsPayload.length,
          attempted: (result.totals && result.totals.attempted) || 0,
          succeeded: (result.totals && result.totals.succeeded) || 0,
          skipped:   (result.totals && result.totals.skipped)   || 0
        };
        // Pull the per-call detail now that we have the batchRunId
        if (result.batchRunId) {
          const snap = await pollAutorecallBatchStatus(result.batchRunId);
          if (snap && snap.calls) this.batchCalls = snap.calls;
        }
        // Extract skipped reasons from events
        if (result.events) {
          this.skippedReasons = result.events
            .filter(e => e.type === 'call_skipped')
            .map(e => ({ patNum: e.patNum, reason: e.reason, message: e.message }));
        }
        this.$emit('call-completed', { mode: 'batch', result });
      } catch (err) {
        this.batchStatus = 'failed';
        this.skippedReasons = [{
          patNum: 0,
          reason: 'network_error',
          message: (err.response && err.response.data && err.response.data.message) || err.message
        }];
      } finally {
        this.step = 'finished';
      }
    },

    // Polling isn't currently invoked because the server doesn't surface the
    // batchRunId until POST completes. Kept as scaffolding for the follow-up
    // that switches the batch Lambda to async-invoke.
    startPolling() {
      this.stopPolling();
      this.pollTimer = setInterval(async () => {
        if (!this.batchRunId) return;
        try {
          const snap = await pollAutorecallBatchStatus(this.batchRunId);
          if (!snap || !snap.batchRun) return;
          this.batchStatus = snap.batchRun.status;
          this.batchTotals = {
            queued:    snap.batchRun.totalQueued,
            attempted: snap.batchRun.totalAttempted,
            succeeded: snap.batchRun.totalSucceeded,
            skipped:   snap.batchRun.totalSkipped
          };
          this.batchCalls = snap.calls || [];
          if (['completed', 'aborted', 'failed'].includes(snap.batchRun.status)) {
            this.stopPolling();
            this.step = 'finished';
          }
        } catch (e) { /* swallow transient poll errors */ }
      }, POLL_INTERVAL_MS);
    },
    stopPolling() {
      if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; }
    },

    async onAbort() {
      if (!this.batchRunId) {
        this.$toast && this.$toast.info && this.$toast.info('Batch has not reported an ID yet — abort will apply once it does.');
        return;
      }
      this.aborting = true;
      try {
        await abortAutorecallBatch(this.batchRunId);
      } catch (_) { /* server is idempotent; ignore */ }
      finally { this.aborting = false; }
    }
  },
  beforeDestroy() { this.stopPolling(); }
};
</script>

<style scoped>
.ai-call-modal-body { padding: 1.25rem; }

.ai-call-list-choices {
  display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 1rem; margin-top: 1rem;
}
.ai-call-list-choice {
  background: #f8f9fa; border: 1px solid #e9ecef; border-radius: 8px;
  padding: 1rem; text-align: center; cursor: pointer; transition: all 0.15s;
}
.ai-call-list-choice:hover:not(.disabled) { background: #e9ecef; border-color: #0d6efd; }
.ai-call-list-choice.disabled { opacity: 0.5; cursor: not-allowed; }
.ai-call-list-choice-icon { font-size: 2rem; }
.ai-call-list-choice-label { font-weight: 600; margin-top: 0.25rem; }
.ai-call-list-choice-sub { font-size: 0.8rem; margin-top: 0.25rem; }

.ai-call-mode-switch { display: flex; gap: 0.5rem; }
.ai-call-mode-btn {
  flex: 1; background: #f8f9fa; border: 1px solid #e9ecef; border-radius: 6px;
  padding: 0.5rem; cursor: pointer; font-size: 0.9rem;
}
.ai-call-mode-btn.active { background: #0d6efd; color: white; border-color: #0d6efd; }

.ai-call-patient-list {
  max-height: 320px; overflow-y: auto; border: 1px solid #e9ecef; border-radius: 4px;
  padding: 0.25rem;
}
.ai-call-patient-row {
  padding: 0.4rem 0.6rem; border-radius: 4px; cursor: pointer;
}
.ai-call-patient-row:hover { background: #f1f3f5; }
.ai-call-patient-row.selected { background: #e7f1ff; border-left: 3px solid #0d6efd; }
.ai-call-patient-row.batch-row { cursor: default; }
.ai-call-patient-row.batch-row:hover { background: transparent; }
.ai-call-patient-name { font-weight: 500; display: inline-block; }
.ai-call-patient-meta { font-size: 0.8rem; }

.ai-call-options label { margin-bottom: 0.25rem; }

.ai-call-progress-list {
  max-height: 280px; overflow-y: auto; border: 1px solid #e9ecef;
  border-radius: 4px; padding: 0.25rem;
}
.ai-call-progress-row {
  display: flex; align-items: center; padding: 0.4rem 0.6rem;
  border-bottom: 1px solid #f1f3f5;
}
.ai-call-progress-row:last-child { border-bottom: none; }
.ai-call-progress-name { font-weight: 500; }
.ai-call-progress-meta { margin-left: auto; }

.ai-call-provider-picker {
  border: 1px solid #e9ecef;
  border-radius: 4px;
  background: #fff;
}
.ai-call-provider-select-all {
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #e9ecef;
  background: #f8f9fa;
}
.ai-call-provider-list {
  max-height: 200px;
  overflow-y: auto;
  padding: 0.5rem 0.75rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 1rem;
  row-gap: 0.35rem;
}
.ai-call-provider-item {
  min-width: 0;
}
.ai-call-provider-item .custom-control-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>

<!-- Unscoped: AgentSidebar sits at z-index 9998. Bootstrap-Vue wraps every
     b-modal in <div id="..._BV_modal_outer_" style="z-index:1040">, which
     creates a stacking context. Because 1040 < 9998, the whole modal subtree
     renders BEHIND the sidebar regardless of the modal's own z-index.
     Fix: target the outer wrapper via :has() on the AI-Call modal class and
     bump it above the sidebar. Scoped to this modal only — other modals
     elsewhere in the app are untouched. -->
<style>
div[id$="_BV_modal_outer_"]:has(.ai-call-modal-wrapper) {
  z-index: 10050 !important;
}
body:has(.ai-call-modal-wrapper.show) .modal-backdrop.show {
  z-index: 10040 !important;
}
</style>
